import React, { useEffect, useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ChartTooltip } from "recharts";
import { Spinner, Card, OverlayTrigger, Tooltip, Modal, Button, Badge } from "react-bootstrap";
import { FiInfo, FiX } from "react-icons/fi";

const GraficoOrdiniOggi = () => {
  const [dataGrafico, setDataGrafico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const ordini = data?.ORDINI_OGGI?.flat() || [];

        const contaOF4G = ordini.filter(
          (item) => item.tipo_doc === "OF4G",
        ).length;
        const contaAltri = ordini.length - contaOF4G;

        setDataGrafico([
          { name: "Urgenti (OF4G)", value: contaOF4G, color: "#b81f25" }, // Rosso Brand
          { name: "Standard / Altri", value: contaAltri, color: "#e9ecef" }, // Grigio chiaro moderno
        ]);

        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore fetch grafico:", err);
        setLoading(false);
      });
  }, []);

  // Calcolo del totale per visualizzarlo al centro della ciambella
  const totale = useMemo(
    () => dataGrafico.reduce((acc, curr) => acc + curr.value, 0),
    [dataGrafico],
  );

  // Tooltip esplicativo per l'header del widget
  const renderWidgetTooltip = (props) => (
    <Tooltip id="widget-info-tooltip" {...props}>
      <div style={{ textAlign: "left", fontSize: "0.75rem", padding: "4px" }}>
        Questo grafico mostra la proporzione degli ordini caricati in data odierna, 
        isolando i documenti urgenti <strong>(OF4G)</strong> rispetto ai flussi standard.
      </div>
    </Tooltip>
  );

  // Sotto-componente Legenda riutilizzabile per mantenere il codice DRY
  const RenderLegenda = ({ isFullscreen = false }) => (
    <div className={isFullscreen ? "d-flex justify-content-center gap-4 w-100" : "d-flex justify-content-between gap-2"}>
      {dataGrafico.map((item, i) => (
        <div
          key={i}
          className="d-flex flex-column align-items-center flex-fill p-2 rounded-3"
          style={{ background: "#f8f9fa", maxWidth: isFullscreen ? "280px" : "100%" }}
        >
          <div className="d-flex align-items-center mb-1">
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: item.color,
                marginRight: "6px",
              }}
            ></div>
            <span
              style={{
                fontSize: isFullscreen ? "0.75rem" : "0.65rem",
                fontWeight: "700",
                color: "#6c757d",
              }}
            >
              {item.name}
            </span>
          </div>
          <span className="fw-bold" style={{ fontSize: isFullscreen ? "1.2rem" : "0.9rem" }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <Card
        className="border-0 shadow-sm d-flex justify-content-center align-items-center"
        style={{ height: "400px", borderRadius: "15px" }}
      >
        <Spinner animation="grow" variant="danger" />
      </Card>
    );
  }

  return (
    <Card
      className="border-0 card shadow-lg overflow-hidden"
      style={{ borderRadius: "16px", height: "400px", background: "#fff" }}
    >
      {/* Header statico del Widget */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            DISTRIBUZIONE ORDINI OGGI
          </span>
          <Badge bg="dark" className="me-2" style={{ fontSize: "0.55rem", padding: "4px 6px" }}>ESPANDI</Badge>
          
          <OverlayTrigger
            placement="right"
            delay={{ show: 100, hide: 400 }}
            overlay={renderWidgetTooltip}
          >
            <span onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>
      </div>

      {/* Contenitore Grafico Standard */}
      <div style={{ position: "relative", height: "240px", width: "100%", marginTop: "15px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={dataGrafico}
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={90}
              paddingAngle={6}
              dataKey="value"
              stroke="none"
              cornerRadius={5}
            >
              {dataGrafico.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} style={{ outline: "none" }} />
              ))}
            </Pie>
            <ChartTooltip
              cursor={{ fill: "transparent" }}
              contentStyle={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                fontSize: "0.8rem",
                color: "#212529"
              }}
              itemStyle={{ color: "#212529" }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Testo centrale alla ciambella */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <span style={{ fontSize: "0.65rem", color: "#6c757d", fontWeight: "600", textTransform: "uppercase" }}>
            Totale
          </span>
          <h2 className="m-0 fw-bold" style={{ color: "#212529", lineHeight: "1", fontSize: "1.6rem" }}>
            {totale}
          </h2>
        </div>
      </div>

      {/* Legenda Standard */}
      <div className="px-4 pb-4 mt-auto">
        <RenderLegenda isFullscreen={false} />
      </div>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          {/* Header Modale */}
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                DISTRIBUZIONE ORDINI OGGI - VISTA ESPANSA
              </span>
              <OverlayTrigger placement="bottom" overlay={renderWidgetTooltip}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>
            <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
              <FiX size={16} /> Chiudi
            </Button>
          </div>
          
          {/* Box di Rendering del Grafico scalato su monitor XL */}
          <div className="flex-grow-1 p-4 d-flex align-items-center justify-content-center">
            <div className="bg-white shadow-sm rounded-4 border p-5 w-100 d-flex flex-column align-items-center justify-content-center" style={{ maxWidth: "1000px", height: "80vh" }}>
              
              <div style={{ position: "relative", height: "400px", width: "100%", maxWidth: "500px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dataGrafico}
                      cx="50%"
                      cy="50%"
                      innerRadius={120}
                      outerRadius={160}
                      paddingAngle={6}
                      dataKey="value"
                      stroke="none"
                      cornerRadius={8}
                    >
                      {dataGrafico.map((entry, index) => (
                        <Cell key={`cell-fs-${index}`} fill={entry.color} style={{ outline: "none" }} />
                      ))}
                    </Pie>
                    <ChartTooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                        fontSize: "0.95rem"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>

                {/* Testo centrale scalato al centro della ciambella nel modale */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                  }}
                >
                  <span style={{ fontSize: "0.85rem", color: "#6c757d", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Flusso Totale
                  </span>
                  <h1 className="m-0 fw-bold" style={{ color: "#212529", lineHeight: "1", fontSize: "3.2rem", letterSpacing: "-1px" }}>
                    {totale}
                  </h1>
                </div>
              </div>

              {/* Legenda scalata nel modale */}
              <div className="w-100 mt-5 pt-3 border-top border-secondary border-opacity-10">
                <RenderLegenda isFullscreen={true} />
              </div>

            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default GraficoOrdiniOggi;